/**
 * SocialFlowCoordinator — owns the full lifecycle of a social sign-in attempt.
 *
 * Responsibilities:
 *   - Saving/reading pending social state in the session store
 *   - Starting and cancelling the 90-second timeout
 *   - Resolving or rejecting the auth result after the IDP redirects back
 *
 * Uses a full-page redirect flow: redirects the current page to the IDP and
 * returns to `/auth/callback` with `?code=` after the user authenticates.
 *
 * AuthSession delegates all social-specific logic here and only receives
 * callbacks for the two outcomes that affect session state:
 *   - `onAuthComplete(user)` — user is authenticated
 *   - `onNeedsPhone(returnTo)` — user needs to provide a phone number
 *   - `onError(code, message)` — auth failed; store the error for the UI to read
 */

import type { SocialProfile, SocialProvider } from '../../types/results';
import type { User } from '../../types/user';
import { AuthErrorCode, DomainErrorName, SocialFlowStatus } from '../../constants';
import {
  currentRelativeUrl,
  decodeReturnTo,
  encodeReturnTo,
  navigateAfterAuth,
  sanitizeReturnTo,
} from '../auth-session/return-to';
import type {
  SocialFlowCallbacks,
  SocialFlowDeps,
  PendingSocialState,
  SocialPendingPhase,
} from './types';

const SOCIAL_TIMEOUT_MS = 90_000;

export class SocialFlowCoordinator {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly deps: SocialFlowDeps,
    private readonly callbacks: SocialFlowCallbacks,
  ) {}

  //#region Public — called by AuthSession

  /**
   * Full-page redirect flow. Redirects the current page to the IDP; returns
   * to `/auth/callback` with `?code=` after the user authenticates.
   */
  begin(provider: SocialProvider, returnTo?: string): void {
    const target = returnTo ?? currentRelativeUrl() ?? '/';
    this.savePending(provider, target, 'awaiting_redirect');
    this.startTimeout(provider);
    this.deps.service.idpRedirectSignIn(provider, encodeReturnTo(target))
      .catch(err => this.handleInitError(err, provider));
  }

  /**
   * Entry point when a user who is already signed in wants to switch to a
   * different social provider. Uses the same-page redirect flow.
   */
  beginViaLogout(provider: SocialProvider): void {
    this.begin(provider);
  }

  hasPendingAfterLogout(): boolean {
    return this.readPending()?.phase === 'awaiting_post_logout_redirect';
  }

  resumeAfterLogout(): void {
    const pending = this.readPending();
    if (pending?.phase !== 'awaiting_post_logout_redirect') return;
    this.deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PENDING' });
    this.begin(pending.provider, pending.returnTo);
  }

  destroy(): void {
    this.cancelTimeout();
  }

  //#endregion

  //#region Public — SocialAuthListener bridge

  setSocialRedirectState(encodedState: string): void {
    const decoded = decodeReturnTo(encodedState);
    if (!decoded) return;
    const pending = this.readPending();
    if (!pending) return;
    this.deps.sessionStore.dispatch({ type: 'SET_SOCIAL_PENDING', state: { ...pending, returnTo: decoded } });
  }

  cognitoSocialSignInComplete(): void {
    const pending = this.readPending();
    this.deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PENDING' });

    if (!pending?.provider) {
      this.deps.logger.log(SocialFlowStatus.Ignored, { event: 'HUB_COMPLETE' });
      return;
    }

    this.deps.service.authenticateAfterRedirect(pending.provider)
      .then(result => this.resolve(result, pending.returnTo, pending.provider, { stagePending: true }))
      .catch(err => this.reject(err, pending.provider));
  }

  legacySdkSignInComplete(profile: SocialProfile): void {
    const pending = this.readPending();
    this.deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PENDING' });

    this.deps.service.legacySocialSignIn(profile)
      .then(result => this.resolve(result, pending?.returnTo ?? null, profile.provider, { stagePending: false }))
      .catch(err => this.reject(err, profile.provider));
  }

  failSocialAuth(error: string): void {
    this.cancelTimeout();
    this.deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PENDING' });
    this.callbacks.onError(AuthErrorCode.SocialSignInFailed, error);
    navigateAfterAuth(null, this.deps.replaceLocation);
  }

  //#endregion

  //#region Private — redirect flow internals

  private handleInitError(err: unknown, provider: SocialProvider): void {
    const message = err instanceof Error ? err.message : 'Social sign-in failed';
    console.error(`[auth] Social sign-in failed to start (${provider}):`, message);
    this.deps.observability.dispatch({ type: 'social_signin_failure', error: err, provider });
    this.callbacks.onError(AuthErrorCode.SocialSignInFailed, message);
    navigateAfterAuth(null, this.deps.replaceLocation);
  }

  private resolve(
    result: { user?: User; needsPhone?: boolean; profile?: { firstName: string; lastName: string } },
    returnTo: string | null | undefined,
    provider: SocialProvider,
    opts: { stagePending: boolean },
  ): void {
    this.cancelTimeout();
    if (result.needsPhone) {
      if (result.profile) {
        this.deps.sessionStore.dispatch({ type: 'SET_SOCIAL_PROFILE', profile: result.profile });
      }
      this.deps.eventBus.emit({ type: 'needs_phone', provider });
      this.callbacks.onNeedsPhone(sanitizeReturnTo(returnTo ?? null));
      return;
    }
    if (result.user) {
      this.deps.observability.dispatch({ type: 'social_signin_success', provider });
      this.deps.eventBus.emit({ type: 'social_sign_in_complete', user: result.user, provider });
      if (opts.stagePending) {
        // Cognito redirect path: stage auth state in localStorage instead of
        // writing cookies on this page. iOS Safari can drop document.cookie
        // writes during the post-OAuth navigation; the destination page's
        // bootstrap promotes the staged state to cookies where there's no
        // navigation race. See bootstrapEffect.
        this.deps.sessionStore.dispatch({
          type: 'SET_PENDING_AUTH',
          user: result.user,
        });
      } else {
        // Legacy SDK path: no page navigation follows, so write cookies and
        // update session state in place.
        this.callbacks.onAuthComplete(result.user);
      }
      navigateAfterAuth(returnTo ?? null, this.deps.replaceLocation);
      return;
    }
    const error = new Error('Authentication returned no result. Please try again.');
    this.deps.observability.dispatch({ type: 'social_signin_failure', error, provider });
    this.callbacks.onError(AuthErrorCode.SocialSignInFailed, error.message);
    navigateAfterAuth(null, this.deps.replaceLocation);
  }

  private reject(err: unknown, provider: string): void {
    this.cancelTimeout();
    const message = err instanceof Error ? err.message : 'Social sign-in failed';
    this.deps.observability.dispatch({ type: 'social_signin_failure', error: err, provider });
    const isPrivateEmailExposed = err instanceof Error && err.name === DomainErrorName.PrivateEmailExposed;
    const code = isPrivateEmailExposed ? AuthErrorCode.PrivateEmailExposed : AuthErrorCode.SocialSignInFailed;
    this.callbacks.onError(code, message);
    this.deps.service.clearStaleSession().catch(clearErr => {
      this.deps.observability.dispatch({ type: 'clear_stale_session_failure', error: clearErr });
    });
    navigateAfterAuth(null, this.deps.replaceLocation);
  }

  //#endregion

  //#region Private — shared helpers

  private startTimeout(provider: SocialProvider): void {
    this.cancelTimeout();
    this.timeoutId = setTimeout(() => {
      this.timeoutId = null;
      this.deps.sessionStore.dispatch({ type: 'CLEAR_SOCIAL_PENDING' });
      this.callbacks.onError(AuthErrorCode.SocialSignInFailed, 'Sign-in timed out. Please try again.');
      this.deps.observability.dispatch({ type: 'social_signin_failure', error: new Error('timeout'), provider });
    }, SOCIAL_TIMEOUT_MS);
  }

  private cancelTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private savePending(provider: SocialProvider, returnTo: string, phase: SocialPendingPhase): void {
    this.deps.sessionStore.dispatch({ type: 'SET_SOCIAL_PENDING', state: { provider, returnTo, phase } });
  }

  private readPending(): PendingSocialState | null {
    const raw = this.deps.sessionStore.getSocialPending();
    if (!raw) return null;
    if (!raw.provider || !raw.phase) {
      this.deps.logger.log(SocialFlowStatus.MalformedPendingState, { raw });
      return null;
    }
    return raw as PendingSocialState;
  }

  //#endregion
}
